import { isAnimationsEnabled, applyAnimationState } from './theme-utils.ts';

declare global {
  interface Window {
    afAnimateCounter?: (el: HTMLElement) => void;
    afInitScrollAnimations?: () => void;
    afCloseMenu?: (menuId: string) => void;
    afToggleMenu?: (menuId: string, trigger?: HTMLElement | null) => void;
  }
}

// Smooth Count-Up Animation for Numeric Stats
export function animateCounter(el: HTMLElement): void {
  if (el.dataset.counterAnimated === 'true') return;
  el.dataset.counterAnimated = 'true';

  const rawText = el.getAttribute('data-counter-value') || el.textContent || '';
  const match = rawText.trim().match(/^([^\d.]*?)([\d,]+(?:\.\d+)?)(.*)$/);
  if (!match) return;

  const prefix = match[1] || '';
  const numericStr = match[2].replace(/,/g, '');
  const suffix = match[3] || '';
  const targetValue = parseFloat(numericStr);
  if (isNaN(targetValue)) return;

  const hasDecimal = match[2].includes('.');
  const decimalPlaces = hasDecimal ? match[2].split('.')[1].length : 0;
  const duration = 1200; // ms
  const startTime = performance.now();

  function step(currentTime: number): void {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    // Ease-out cubic curve
    const eased = 1 - Math.pow(1 - progress, 3);
    const currentValue = targetValue * eased;

    const formattedNumber = hasDecimal
      ? currentValue.toFixed(decimalPlaces)
      : Math.round(currentValue).toLocaleString();

    el.textContent = prefix + formattedNumber + suffix;

    if (progress < 1) {
      requestAnimationFrame(step);
    } else {
      el.textContent = rawText.trim();
    }
  }

  requestAnimationFrame(step);
}

export function initCounters(container?: HTMLElement | Document): void {
  const scope = container || document;
  const counters = scope.querySelectorAll<HTMLElement>(
    '[data-counter-value], .counter-value, [data-stat-counter]'
  );
  counters.forEach(function (counter) {
    if (
      counter.closest("[data-widget-type='Hero']") ||
      counter.closest('.hero-section') ||
      counter.getAttribute('data-static') === 'true'
    ) {
      return;
    }
    animateCounter(counter);
  });
}

// Scroll Reveal Observer Engine
export function initScrollAnimations(): void {
  if (typeof document === 'undefined') return;

  const enabled = isAnimationsEnabled();
  applyAnimationState(enabled);

  const animElements = document.querySelectorAll<HTMLElement>(
    '[data-animate="true"], [data-scroll-reveal]'
  );

  if (
    !enabled ||
    (typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      Boolean(window.matchMedia('(prefers-reduced-motion: reduce)')?.matches))
  ) {
    animElements.forEach(function (el) {
      el.setAttribute('data-animated', 'true');
      initCounters(el);
    });
    return;
  }

  if (typeof IntersectionObserver === 'undefined') {
    animElements.forEach(function (el) {
      el.setAttribute('data-animated', 'true');
      initCounters(el);
    });
    return;
  }

  const observer = new IntersectionObserver(
    function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          const target = entry.target as HTMLElement;
          target.setAttribute('data-animated', 'true');
          initCounters(target);
          if (obs && typeof obs.unobserve === 'function') {
            obs.unobserve(target);
          }
        }
      });
    },
    {
      threshold: 0.01,
      rootMargin: '200px 0px 200px 0px',
    }
  );

  animElements.forEach(function (el) {
    const widgetType = el.getAttribute('data-widget-type');
    const isAboveFold =
      widgetType === 'Hero' ||
      widgetType === 'Nav' ||
      widgetType === 'Footer' ||
      el.tagName.toLowerCase() === 'footer' ||
      el.id === 'af-main-nav' ||
      el.classList.contains('hero-section');

    if (el.getAttribute('data-animate') === 'false' || isAboveFold) {
      el.setAttribute('data-animated', 'true');
      initCounters(el);
    } else {
      observer.observe(el);
    }
  });

  // Safety fallback: ensure all animated elements become visible within 600ms
  setTimeout(function () {
    animElements.forEach(function (el) {
      if (el.getAttribute('data-animated') !== 'true') {
        el.setAttribute('data-animated', 'true');
        initCounters(el);
      }
    });
  }, 600);
}

// Sticky Navigation Glassmorphism on Scroll
export function initStickyNavScroll(): void {
  const nav =
    document.getElementById('af-main-nav') ||
    document.querySelector<HTMLElement>('[data-widget-type="Nav"]') ||
    document.querySelector<HTMLElement>('header nav');
  if (!nav || nav.dataset.scrollBound === 'true') return;
  nav.dataset.scrollBound = 'true';

  function handleScroll(): void {
    const isScrolled = window.scrollY > 20;
    nav!.classList.toggle('nav-scrolled', isScrolled);
    if (isScrolled) {
      nav!.classList.add('shadow-md');
    } else {
      nav!.classList.remove('shadow-md');
    }
  }

  let ticking = false;
  function onScroll(): void {
    if (!ticking) {
      window.requestAnimationFrame(function () {
        handleScroll();
        ticking = false;
      });
      ticking = true;
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  if (typeof window !== 'undefined' && window.requestAnimationFrame) {
    window.requestAnimationFrame(handleScroll);
  } else {
    setTimeout(handleScroll, 0);
  }
}

// Global navigation menu functions
export function closeMenu(menuId: string): void {
  const menu = document.getElementById(menuId);
  if (!menu) return;
  menu.classList.add('hidden');
  const toggles = document.querySelectorAll('[aria-controls="' + menuId + '"]');
  toggles.forEach(function (toggle) {
    toggle.setAttribute('aria-expanded', 'false');
    const icon = toggle.querySelector('.material-symbols-outlined');
    if (icon) icon.textContent = 'menu';
  });
}

export function toggleMenuGlobal(menuId: string, trigger?: HTMLElement | null): void {
  const menu = document.getElementById(menuId);
  if (!menu) return;
  const isClosed = menu.classList.contains('hidden');
  if (isClosed) {
    menu.classList.remove('hidden');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
      const icon = trigger.querySelector('.material-symbols-outlined');
      if (icon) icon.textContent = 'close';
    }
  } else {
    menu.classList.add('hidden');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      const icon = trigger.querySelector('.material-symbols-outlined');
      if (icon) icon.textContent = 'menu';
    }
  }
}

export function setupMobileMenu(): void {
  const toggleBtn =
    document.getElementById('af-mobile-menu-btn') ||
    document.querySelector<HTMLElement>('[data-mobile-menu-toggle]') ||
    document.querySelector<HTMLElement>('[aria-controls="af-mobile-menu"]');

  const menu =
    document.getElementById('af-mobile-menu') ||
    document.querySelector<HTMLElement>('[data-mobile-menu]');

  if (!toggleBtn || !menu) return;
  if (toggleBtn.dataset.bound === 'true') return;
  toggleBtn.dataset.bound = 'true';

  const icon =
    toggleBtn.querySelector<HTMLElement>('.material-symbols-outlined') ||
    toggleBtn.querySelector<HTMLElement>('[data-menu-icon]');

  function openMenu(): void {
    menu!.classList.remove('hidden');
    toggleBtn!.setAttribute('aria-expanded', 'true');
    if (icon) icon.textContent = 'close';
  }

  function closeMenuLocal(): void {
    menu!.classList.add('hidden');
    toggleBtn!.setAttribute('aria-expanded', 'false');
    if (icon) icon.textContent = 'menu';
  }

  function toggleMenuLocal(): void {
    const isClosed = menu!.classList.contains('hidden');
    if (isClosed) {
      openMenu();
    } else {
      closeMenuLocal();
    }
  }

  toggleBtn.addEventListener('click', function (e) {
    e.preventDefault();
    e.stopPropagation();
    toggleMenuLocal();
  });

  toggleBtn.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleMenuLocal();
    }
  });

  menu.querySelectorAll('a').forEach(function (link) {
    link.addEventListener('click', function () {
      closeMenuLocal();
    });
  });

  document.addEventListener('click', function (e) {
    if (!menu!.classList.contains('hidden')) {
      const target = e.target as Node | null;
      if (target && !menu!.contains(target) && !toggleBtn!.contains(target)) {
        closeMenuLocal();
      }
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !menu!.classList.contains('hidden')) {
      closeMenuLocal();
      toggleBtn!.focus();
    }
  });

  window.addEventListener(
    'resize',
    function () {
      if (window.innerWidth >= 768 && !menu!.classList.contains('hidden')) {
        closeMenuLocal();
      }
    },
    { passive: true }
  );
}

if (typeof window !== 'undefined') {
  window.afAnimateCounter = animateCounter;
  window.afInitScrollAnimations = initScrollAnimations;
  window.afCloseMenu = closeMenu;
  window.afToggleMenu = toggleMenuGlobal;
}
